import useSWR from "swr"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { createTransferApplicationsKey } from "../constants/swrKeys"
import type {
  UseGetTransferApplicationsParams,
  UseGetTransferApplicationsReturn,
  TransferApplicationsApiResponse,
  TransferApplication,
  Pagination,
} from "../types"

/**
 * 立替送金申請一覧を取得するカスタムフック
 *
 * @param params - 取得パラメータ
 * @param params.transferApplicationId - 特定の申請IDで絞り込み（オプション）
 * @param params.page - ページ番号（デフォルト: 1）
 * @returns 立替送金申請一覧とその操作メソッド
 *
 * @example
 * ```typescript
 * // 基本的な使用方法
 * const { transferApplications, isLoading, error } = useGetTransferApplications()
 *
 * // ページネーション付き
 * const { transferApplications, pagination } = useGetTransferApplications({ page: 2 })
 *
 * // 特定IDで絞り込み
 * const { transferApplications } = useGetTransferApplications({
 *   transferApplicationId: 123
 * })
 * ```
 */
export const useGetTransferApplications = (
  params: UseGetTransferApplicationsParams = {}
): UseGetTransferApplicationsReturn => {
  const { getWithToken } = useSmoothApi()

  // SWRキーを生成
  const swrKey = createTransferApplicationsKey(params)

  /**
   * データ取得用のfetcher関数
   * @returns APIレスポンス
   */
  const fetcher = async (): Promise<TransferApplicationsApiResponse> => {
    try {
      const [url] = swrKey
      const response = await getWithToken(url)
      return response.data as TransferApplicationsApiResponse
    } catch (error) {
      // エラーログ出力（開発環境での診断用）
      console.error("立替送金申請一覧の取得に失敗しました:", error)
      throw error
    }
  }

  // SWRでデータフェッチング
  const swr = useSWR(swrKey, fetcher, {
    // 再検証の設定
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    // エラー時の再試行設定
    errorRetryCount: 3,
    errorRetryInterval: 1000,
  })

  // データの抽出と型安全性の確保
  const transferApplications: TransferApplication[] = swr.data?.transferApplications || []
  const pagination: Pagination | undefined = swr.data?.pagination
  const isLoading = !swr.data && !swr.error
  const error = swr.error
  const isEmpty = !isLoading && transferApplications.length === 0

  /**
   * データを強制的にリフレッシュする関数
   * キャッシュをクリアして新しいデータを取得
   */
  const refresh = async () => {
    return swr.mutate(undefined, { revalidate: true })
  }

  return {
    // データ
    transferApplications,
    pagination,
    data: swr.data,

    // 状態
    isLoading,
    error,
    isEmpty,

    // 操作メソッド
    mutate: swr.mutate,
    refresh,
  }
}
